import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  CreditCard, Settings as SettingsIcon, TrendingUp, AlertTriangle, CheckCircle,
  ExternalLink, Zap, Plug, ShieldCheck, Palette, Globe, Code, Copy, Check, Users, Mail,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { normalizeDomainWhitelist } from '@harbormaster/shared';
import { api, ApiError, uploadBerthPlanImage, deleteBerthPlanImage } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getDateLocale } from '../i18n';

// ── Types ────────────────────────────────────────────────────────

type SettingsTab = 'general' | 'branding' | 'payments' | 'team' | 'billing';

interface MarinaConfig {
  name: string;
  slug: string;
  logo_url: string | null;
  berth_plan_image_url?: string | null;
  berth_plan_public?: boolean;
  primary_color: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  staff_notifications_enabled?: boolean;
  staff_notifications_email?: string | null;
  contact_vhf: string | null;
  address: string | null;
  active_languages: string[];
  season_open: string | null;
  season_close: string | null;
  domain_whitelist: string[];
  security?: { domain_whitelist_missing?: boolean };
}

interface SyncSubDetail {
  sub_id: string;
  status: string;
  price_id: string;
  matched: boolean;
}

interface StripeSyncInfo {
  customer_id: string;
  stripe_subscription_total: number;
  platform_subscriptions_matched: number;
  unmatched_price_ids: string[];
  status_inferred_from_unmatched?: boolean;
  configured_price_ids?: Record<string, string>;
  subscriptions?: SyncSubDetail[];
}

interface BillingData {
  tier: string;
  status: string;
  trial_ends_at: string | null;
  trial_days_left: number | null;
  subscription_renews_at: string | null;
  chats_used: number;
  chat_limit: number;
  has_billing_account: boolean;
  price_ids: Record<string, string>;
  publishable_key: string | null;
  stripe_sync?: StripeSyncInfo;
}

interface PaymentsData {
  connected: boolean;
  onboarding_complete: boolean;
  payment_currency: string;
  platform_fee_percent: number;
  cancellation_policy: Record<string, number> | null;
}

// ── Constants ────────────────────────────────────────────────────

const ALL_LANGUAGE_CODES = ['et', 'en', 'fi', 'de'];

const TIER_LABELS: Record<string, string> = {
  free: 'Free', starter: 'Starter', growth: 'Growth', group: 'Group', enterprise: 'Enterprise',
};
const TIER_COLORS: Record<string, string> = {
  free: 'bg-slate-100 text-slate-800',
  starter: 'bg-blue-100 text-blue-800',
  growth: 'bg-purple-100 text-purple-800',
  group: 'bg-orange-100 text-orange-800',
  enterprise: 'bg-gray-100 text-gray-800',
};
const STATUS_COLORS: Record<string, string> = {
  trialing: 'bg-blue-50 text-blue-700 border-blue-200',
  active: 'bg-green-50 text-green-700 border-green-200',
  past_due: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-700 border-gray-200',
};
const PLAN_OPTIONS = [
  { id: 'starter', label: 'Starter', price: 79, chats: 150, marinas: 1, description: 'Single marina — up to 150 new AI conversations per month.' },
  { id: 'growth', label: 'Growth', price: 199, chats: 500, marinas: 3, description: 'Up to 3 marinas — 500 new conversations per month.' },
  { id: 'group', label: 'Group', price: 499, chats: 1500, marinas: 10, description: 'Up to 10 locations — 1,500 new conversations per month.' },
];

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';
const MM_DD = /^\d{2}-\d{2}$/;
const STAFF_NOTIFICATIONS_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Component ────────────────────────────────────────────────────

export function SettingsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: SettingsTab = (searchParams.get('tab') as SettingsTab) ?? 'general';
  const { isOrgAdmin, activeMarinaId, profile } = useAuth();

  /** Org manages multiple marinas but none selected in header — marina-scoped API calls must not run. */
  const needsMarinaSelection =
    isOrgAdmin &&
    (profile?.accessibleMarinaIds?.length ?? 0) > 1 &&
    activeMarinaId === null;
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // ── Marina config state (General + Branding tabs) ─────────────
  const [config, setConfig] = useState<MarinaConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const [color, setColor] = useState('#2563eb');
  const [logoUrl, setLogoUrl] = useState('');
  const [berthPlanUrl, setBerthPlanUrl] = useState('');
  const [berthPlanPublic, setBerthPlanPublic] = useState(true);
  const [berthPlanUploading, setBerthPlanUploading] = useState(false);
  const [languages, setLanguages] = useState<string[]>([]);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [vhf, setVhf] = useState('');
  const [address, setAddress] = useState('');
  const [seasonOpen, setSeasonOpen] = useState('');
  const [seasonClose, setSeasonClose] = useState('');
  const [domains, setDomains] = useState('');
  const [domainWhitelistMissing, setDomainWhitelistMissing] = useState(false);
  const [staffNotifyEnabled, setStaffNotifyEnabled] = useState(false);
  const [staffNotifyEmail, setStaffNotifyEmail] = useState('');

  const fetchConfig = useCallback(async () => {
    if (needsMarinaSelection) {
      setConfig(null);
      setConfigLoading(false);
      return;
    }
    setConfigLoading(true);
    try {
      const data = await api.getMarina() as unknown as MarinaConfig;
      setConfig(data);
      setDomainWhitelistMissing(Boolean(data.security?.domain_whitelist_missing));
      setColor(data.primary_color ?? '#2563eb');
      setLogoUrl(data.logo_url ?? '');
      setBerthPlanUrl(data.berth_plan_image_url ?? '');
      setBerthPlanPublic((data as { berth_plan_public?: boolean }).berth_plan_public !== false);
      setLanguages(data.active_languages ?? []);
      setPhone(data.contact_phone ?? '');
      setEmail(data.contact_email ?? '');
      setVhf(data.contact_vhf ?? '');
      setAddress(data.address ?? '');
      setSeasonOpen(data.season_open ?? '');
      setSeasonClose(data.season_close ?? '');
      setDomains((data.domain_whitelist ?? []).join('\n'));
      setStaffNotifyEnabled(data.staff_notifications_enabled === true);
      setStaffNotifyEmail(typeof data.staff_notifications_email === 'string' ? data.staff_notifications_email : '');
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setConfigLoading(false);
    }
  }, [activeMarinaId, needsMarinaSelection]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    if (saving) return;
    if (needsMarinaSelection) return;
    if (languages.length === 0) { toast.error(t('settings.atLeastOneLang')); return; }
    if (seasonOpen && !MM_DD.test(seasonOpen)) { toast.error('Season open must be in MM-DD format (e.g. 05-01)'); return; }
    if (seasonClose && !MM_DD.test(seasonClose)) { toast.error('Season close must be in MM-DD format (e.g. 09-30)'); return; }
    if (staffNotifyEnabled) {
      const se = staffNotifyEmail.trim();
      if (!se || !STAFF_NOTIFICATIONS_EMAIL_RE.test(se)) {
        toast.error(t('settings.staffNotificationsEmailInvalid'));
        return;
      }
      if (se.length > 320) {
        toast.error(t('settings.staffNotificationsEmailInvalid'));
        return;
      }
    }
    setSaving(true);
    try {
      const rawLines = domains.split('\n').map((d) => d.trim()).filter(Boolean);
      const wl = normalizeDomainWhitelist(rawLines);
      if (rawLines.length > 0 && wl.length === 0) {
        toast.error(t('settings.domainWhitelistInvalid'));
        setSaving(false);
        return;
      }
      await api.updateMarina({
        primary_color: color || null,
        logo_url: logoUrl || null,
        berth_plan_image_url: berthPlanUrl.trim() || null,
        berth_plan_public: berthPlanPublic,
        active_languages: languages,
        contact_phone: phone || null,
        contact_email: email || null,
        contact_vhf: vhf || null,
        address: address || null,
        season_open: seasonOpen || null,
        season_close: seasonClose || null,
        domain_whitelist: wl,
        staff_notifications_enabled: staffNotifyEnabled,
        staff_notifications_email: staffNotifyEmail.trim() || null,
      });
      toast.success(t('settings.settingsSaved'));
      await fetchConfig();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const toggleLanguage = (code: string) =>
    setLanguages((prev) => prev.includes(code) ? prev.filter((l) => l !== code) : [...prev, code]);

  const widgetBase = import.meta.env.VITE_WIDGET_URL ?? 'https://harbormaster.app';
  const apiBase = import.meta.env.VITE_API_URL ?? '';
  const embedCode = config
    ? (() => {
        const params = new URLSearchParams({ marina: config.slug });
        if (apiBase) params.set('api', apiBase);
        return `<script src="${widgetBase}/widget.js?${params.toString()}" async></script>`;
      })()
    : '';

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      toast.success(t('settings.embedCodeCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error(t('settings.copyFailed')); }
  };

  // ── Billing state ─────────────────────────────────────────────
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [reconcileError, setReconcileError] = useState<string | null>(null);

  // Marina selection modal state (shown on downgrade when marina count exceeds new limit)
  const [marinaSelection, setMarinaSelection] = useState<{
    marinas: { id: number; name: string; slug: string }[];
    newLimit: number;
    priceId: string;
  } | null>(null);
  const [selectedKeepIds, setSelectedKeepIds] = useState<Set<number>>(new Set());

  const fetchBilling = useCallback(async (showLoader = true) => {
    if (showLoader) setBillingLoading(true);
    setReconcileError(null);
    try {
      const data = await api.reconcileBilling();
      setBilling(data as unknown as BillingData);
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.code}: ${err.message}` : 'reconcile failed';
      setReconcileError(msg);
      try {
        const data = await api.getBilling();
        setBilling(data as unknown as BillingData);
      } catch (e) {
        if (e instanceof ApiError) toastRef.current.error(e.message);
      }
    } finally {
      setBillingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'billing' || !isOrgAdmin) return;
    fetchBilling();
  }, [activeTab, isOrgAdmin, fetchBilling]);

  // Re-reconcile when the tab regains visibility (e.g. returning from Stripe portal)
  useEffect(() => {
    if (activeTab !== 'billing' || !isOrgAdmin) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchBilling(false);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [activeTab, isOrgAdmin, fetchBilling]);

  const handleUpgrade = async (priceId: string, keepMarinaIds?: number[]) => {
    if (!priceId) { toast.error(t('settings.pricingNotConfigured')); return; }
    setActionLoading('checkout');
    try {
      const { checkoutUrl, subscriptionUpdated } = await api.createCheckoutSession(priceId, keepMarinaIds);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      if (subscriptionUpdated) {
        toast.success(t('settings.planUpdatedInPlace'));
        setMarinaSelection(null);
        setBilling(null);
        fetchBilling();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'MARINA_SELECTION_REQUIRED') {
          const body = err.raw as { marinas?: { id: number; name: string; slug: string }[]; new_limit?: number };
          if (body?.marinas && body.new_limit) {
            setMarinaSelection({ marinas: body.marinas, newLimit: body.new_limit, priceId });
            setSelectedKeepIds(new Set());
          }
        } else if (err.code === 'MULTIPLE_PLATFORM_SUBSCRIPTIONS') {
          toast.error(t('settings.multipleSubscriptionsToast'));
        } else {
          toast.error(err.message);
        }
      }
    } finally { setActionLoading(null); }
  };

  const handleManageBilling = async () => {
    setActionLoading('portal');
    try {
      const { portalUrl } = await api.getBillingPortal();
      if (portalUrl) window.open(portalUrl, '_blank');
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message);
    } finally { setActionLoading(null); }
  };

  // ── Payments state ────────────────────────────────────────────
  const [payments, setPayments] = useState<PaymentsData | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'payments' && !payments) {
      setPaymentsLoading(true);
      api.getPaymentStatus()
        .then((data) => setPayments(data))
        .catch((err) => { if (err instanceof ApiError) toast.error(err.message); })
        .finally(() => setPaymentsLoading(false));
    }
  }, [activeTab, payments, toast]);

  // Handle Stripe Connect + checkout redirects on mount
  useEffect(() => {
    const stripe = searchParams.get('stripe');
    const checkout = searchParams.get('checkout');
    if (stripe === 'complete') {
      toast.success(t('settings.stripeConnected'));
      setSearchParams({ tab: 'payments' }); setPayments(null);
    } else if (stripe === 'refresh') {
      toast.info(t('settings.stripeIncomplete'));
      setSearchParams({ tab: 'payments' });
    } else if (checkout === 'success') {
      toast.success(t('settings.subscriptionActivated'));
      setSearchParams({ tab: 'billing' }); setBilling(null);
    } else if (checkout === 'cancelled') {
      toast.info(t('settings.checkoutCancelled'));
      setSearchParams({ tab: 'billing' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnectStripe = async () => {
    setActionLoading('connect');
    try {
      const { url } = await api.connectStripe();
      if (url) window.location.href = url;
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message);
    } finally { setActionLoading(null); }
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm(t('settings.disconnectStripeConfirm'))) return;
    setActionLoading('disconnect');
    try {
      await api.disconnectStripe();
      toast.success(t('settings.stripeIncomplete'));
      setPayments(null);
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message);
    } finally { setActionLoading(null); }
  };

  // ── Tab helper ────────────────────────────────────────────────
  function setTab(tab: SettingsTab) { setSearchParams({ tab }); }
  const usagePct =
    billing && billing.chat_limit > 0
      ? Math.min(100, Math.round((billing.chats_used / billing.chat_limit) * 100))
      : 0;

  // ── Render ────────────────────────────────────────────────────

  const TABS: { id: SettingsTab; label: string; icon?: React.ComponentType<{ className?: string }>; adminOnly?: boolean }[] = [
    { id: 'general', label: t('settings.tabs.general') },
    { id: 'branding', label: t('settings.tabs.branding'), icon: Palette },
    { id: 'payments', label: t('settings.tabs.payments'), icon: Plug },
    { id: 'team', label: t('settings.tabs.team'), icon: Users },
    { id: 'billing', label: t('settings.tabs.billing'), icon: CreditCard, adminOnly: true },
  ];

  const tabClass = (id: SettingsTab) =>
    `flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
      activeTab === id
        ? 'bg-white border border-b-white border-gray-200 text-brand-600 -mb-px'
        : 'text-gray-500 hover:text-gray-700'
    }`;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      <h1 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
        <SettingsIcon className="w-5 h-5" />
        {t('settings.title')}
      </h1>

      {/* Tab navigation */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-6">
        {TABS.filter((t) => !t.adminOnly || isOrgAdmin).map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={tabClass(t.id)}>
            {t.icon && <t.icon className="w-3.5 h-3.5" />}
            {t.label}
          </button>
        ))}
      </div>

      {needsMarinaSelection && (
        <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-950 text-sm">
          {t('settings.selectMarinaHint')}
        </div>
      )}

      {/* ── GENERAL TAB ─────────────────────────────────────────── */}
      {activeTab === 'general' && (
        <div className="space-y-6 max-w-2xl">
          {configLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Marina identity (read-only info) */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                <h2 className="text-sm font-semibold text-gray-900">{t('settings.marinaIdentity')}</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>{t('settings.marinaName')}</label>
                    <p className="text-sm font-medium text-gray-800 py-2">{config?.name}</p>
                  </div>
                  <div>
                    <label className={labelClass}>{t('settings.slug')}</label>
                    <p className="text-sm font-mono text-gray-500 py-2">{config?.slug}</p>
                  </div>
                </div>
              </div>

              {/* Contact & season */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <SettingsIcon className="w-4 h-4 text-brand-600" /> {t('settings.contactSeason')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>{t('settings.phone')}</label>
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+372…" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('settings.email')}</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('settings.vhfChannel')}</label>
                    <input type="text" value={vhf} onChange={(e) => setVhf(e.target.value)} placeholder="Ch 16" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('settings.address')}</label>
                    <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('settings.seasonOpen')}</label>
                    <input type="text" value={seasonOpen} onChange={(e) => setSeasonOpen(e.target.value)} placeholder="05-01" maxLength={5} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('settings.seasonClose')}</label>
                    <input type="text" value={seasonClose} onChange={(e) => setSeasonClose(e.target.value)} placeholder="10-01" maxLength={5} className={inputClass} />
                  </div>
                </div>
              </div>

              {/* Staff operational email alerts */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-brand-600" /> {t('settings.staffNotifications')}
                </h2>
                <p className="text-xs text-gray-500 leading-relaxed">{t('settings.staffNotificationsHint')}</p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={staffNotifyEnabled}
                    onChange={(e) => setStaffNotifyEnabled(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-800">{t('settings.staffNotificationsEnable')}</span>
                </label>
                <div>
                  <label className={labelClass}>{t('settings.staffNotificationsEmail')}</label>
                  <input
                    type="email"
                    value={staffNotifyEmail}
                    onChange={(e) => setStaffNotifyEmail(e.target.value)}
                    placeholder={t('settings.staffNotificationsEmailPlaceholder')}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Languages */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-brand-600" /> {t('settings.languages')}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {ALL_LANGUAGE_CODES.map((code) => {
                    const isActive = languages.includes(code);
                    const isLast = isActive && languages.length === 1;
                    return (
                      <button
                        key={code}
                        onClick={() => !isLast && toggleLanguage(code)}
                        disabled={isLast}
                        title={isLast ? t('settings.atLeastOneLang') : undefined}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                          isActive ? 'bg-brand-50 border-brand-300 text-brand-700' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                        } ${isLast ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {t(`settings.languageOptions.${code}` as Parameters<typeof t>[0])}
                      </button>
                    );
                  })}
                </div>
                {languages.length === 0 && <p className="text-xs text-red-600">{t('settings.atLeastOneLang')}</p>}
              </div>

              {/* Domain whitelist */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Code className="w-4 h-4 text-brand-600" /> {t('settings.domainWhitelist')}
                </h2>
                {domainWhitelistMissing && (
                  <div className="flex gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-950 text-sm">
                    <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                    <span>{t('settings.domainWhitelistSecurityWarning')}</span>
                  </div>
                )}
                <textarea
                  value={domains}
                  onChange={(e) => setDomains(e.target.value)}
                  rows={3}
                  placeholder={t('settings.domainWhitelistPlaceholder')}
                  className={inputClass + ' font-mono'}
                />
                <p className="text-xs text-gray-400">{t('settings.domainWhitelistHint')}</p>
              </div>

              <button onClick={handleSave} disabled={saving || needsMarinaSelection} className="px-6 py-2.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors">
                {saving ? t('common.saving') : t('settings.saveSettings')}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── BRANDING TAB ────────────────────────────────────────── */}
      {activeTab === 'branding' && (
        <div className="space-y-6 max-w-2xl">
          {configLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-brand-600" /> {t('settings.branding')}
                </h2>
                <div>
                  <label className={labelClass}>{t('settings.brandColor')}</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5" />
                    <input type="text" value={color} onChange={(e) => setColor(e.target.value)} maxLength={7} className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/40" />
                    <div className="w-8 h-8 rounded-full border border-gray-200" style={{ backgroundColor: color }} title="Preview" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>{t('settings.logoUrl')}</label>
                  <input type="url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://…" className={inputClass} />
                  <p className="text-xs text-gray-400 mt-1">{t('settings.logoUrlHint')}</p>
                </div>
                {logoUrl && (
                  <div>
                    <label className={labelClass}>{t('settings.preview')}</label>
                    <img src={logoUrl} alt="Logo preview" className="w-16 h-16 rounded-lg border border-gray-200 object-contain bg-gray-50" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  </div>
                )}
                <div className="pt-2 border-t border-gray-100 space-y-3">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('settings.berthPlanImage')}</h3>
                  <p className="text-xs text-gray-500">{t('settings.berthPlanImageHint')}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex">
                      <span className="sr-only">{t('settings.berthPlanUpload')}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        disabled={berthPlanUploading || needsMarinaSelection}
                        className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (!f) return;
                          setBerthPlanUploading(true);
                          try {
                            const { berth_plan_image_url } = await uploadBerthPlanImage(f);
                            setBerthPlanUrl(berth_plan_image_url);
                            toast.success(t('settings.berthPlanUploadSuccess'));
                          } catch (err) {
                            toast.error(err instanceof ApiError ? err.message : t('errors.failedToSave'));
                          } finally {
                            setBerthPlanUploading(false);
                          }
                        }}
                      />
                    </label>
                    {berthPlanUrl ? (
                      <button
                        type="button"
                        disabled={berthPlanUploading || needsMarinaSelection}
                        onClick={async () => {
                          setBerthPlanUploading(true);
                          try {
                            await deleteBerthPlanImage();
                            setBerthPlanUrl('');
                            toast.success(t('settings.berthPlanRemoved'));
                          } catch (err) {
                            toast.error(err instanceof ApiError ? err.message : t('errors.failedToSave'));
                          } finally {
                            setBerthPlanUploading(false);
                          }
                        }}
                        className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {t('settings.berthPlanRemove')}
                      </button>
                    ) : null}
                  </div>
                  <div>
                    <label className={labelClass}>{t('settings.berthPlanUrlOptional')}</label>
                    <input
                      type="url"
                      value={berthPlanUrl}
                      onChange={(e) => setBerthPlanUrl(e.target.value)}
                      placeholder="https://…"
                      className={inputClass}
                    />
                  </div>
                  {berthPlanUrl ? (
                    <div>
                      <label className={labelClass}>{t('settings.preview')}</label>
                      <img
                        src={berthPlanUrl}
                        alt=""
                        className="max-h-48 w-full max-w-md rounded-lg border border-gray-200 object-contain bg-gray-50"
                        onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  ) : null}
                  {berthPlanUrl ? (
                    <div className="pt-1">
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500/40"
                          checked={berthPlanPublic}
                          onChange={(e) => setBerthPlanPublic(e.target.checked)}
                          disabled={needsMarinaSelection}
                        />
                        <span>
                          <span className="block text-sm text-gray-800">{t('settings.berthPlanPublic')}</span>
                          <span className="block text-xs text-gray-500 mt-0.5">{t('settings.berthPlanPublicHint')}</span>
                        </span>
                      </label>
                    </div>
                  ) : null}
                </div>
              </div>

              <button onClick={handleSave} disabled={saving || needsMarinaSelection} className="px-6 py-2.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors">
                {saving ? t('common.saving') : t('settings.saveBranding')}
              </button>

              {/* Embed code */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Code className="w-4 h-4 text-brand-600" /> {t('settings.embedCode')}
                </h2>
                <p className="text-xs text-gray-500">{t('settings.embedCodeHint')}</p>
                <div className="relative">
                  <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap break-all">
                    {embedCode}
                  </pre>
                  <button onClick={copyEmbed} className="absolute top-2 right-2 p-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 text-gray-500">
                    {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── TEAM TAB ────────────────────────────────────────────── */}
      {activeTab === 'team' && (
        <div className="max-w-md space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
            <Users className="w-10 h-10 text-brand-600 mx-auto mb-3" />
            <h2 className="text-base font-semibold text-gray-900 mb-1">{t('settings.teamManagement')}</h2>
            <p className="text-sm text-gray-500 mb-4">{t('settings.teamManagementSubtitle')}</p>
            <Link
              to="/team"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Users className="w-4 h-4" />
              {t('settings.goToTeamPage')}
            </Link>
          </div>
        </div>
      )}

      {/* ── PAYMENTS TAB ────────────────────────────────────────── */}
      {activeTab === 'payments' && (
        <div className="space-y-6 max-w-2xl">
          {paymentsLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!paymentsLoading && payments && (
            <>
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Plug className="w-4 h-4" /> Stripe Connect
                  </h2>
                  {payments.connected && payments.onboarding_complete ? (
                    <span className="flex items-center gap-1.5 text-xs font-medium bg-green-100 text-green-800 px-2.5 py-1 rounded-full">
                      <CheckCircle className="w-3.5 h-3.5" /> {t('settings.connected')}
                    </span>
                  ) : payments.connected ? (
                    <span className="flex items-center gap-1.5 text-xs font-medium bg-yellow-100 text-yellow-800 px-2.5 py-1 rounded-full">
                      <AlertTriangle className="w-3.5 h-3.5" /> {t('settings.onboardingIncomplete')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{t('settings.notConnected')}</span>
                  )}
                </div>

                {payments.connected && payments.onboarding_complete ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <ShieldCheck className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-800">{t('settings.onlinePaymentsEnabled')}</p>
                        <p className="text-xs text-green-700 mt-0.5">{t('settings.platformFee', { fee: payments.platform_fee_percent })}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors">
                        <ExternalLink className="w-3.5 h-3.5" /> {t('settings.stripeDashboard')}
                      </a>
                      <button onClick={handleDisconnectStripe} disabled={actionLoading === 'disconnect'} className="px-3 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40">{t('settings.disconnect')}</button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">{t('settings.connectStripeDesc')}</p>
                    <div className="grid grid-cols-3 gap-3 text-center text-xs text-gray-500">
                      {[['💳', t('settings.guestPaysOnline'), t('settings.cardPaymentAtBooking')], ['🏦', t('settings.directToYou'), t('settings.moneyInYourStripe')], ['%', `${payments.platform_fee_percent}%`, t('settings.feePerPayment')]].map(([icon, title, sub]) => (
                        <div key={title} className="bg-gray-50 rounded-lg p-3">
                          <div className="text-lg mb-1">{icon}</div>
                          <p className="font-medium text-gray-700">{title}</p>
                          <p>{sub}</p>
                        </div>
                      ))}
                    </div>
                    <button onClick={handleConnectStripe} disabled={actionLoading === 'connect'} className="flex items-center gap-2 px-4 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-40">
                      {actionLoading === 'connect' ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plug className="w-4 h-4" />}
                      {t('settings.connectStripe')}
                    </button>
                  </div>
                )}
              </div>

              {payments.connected && payments.onboarding_complete && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h2 className="text-base font-semibold text-gray-900 mb-1">{t('settings.cancellationPolicy')}</h2>
                  <p className="text-sm text-gray-500 mb-4">{t('settings.cancellationPolicySubtitle')}</p>
                  <div className="grid grid-cols-3 gap-4">
                    {[{ key: '48h', label: '>48h before arrival' }, { key: '24h', label: '24–48h before' }, { key: '0h', label: '<24h before' }].map(({ key, label }) => (
                      <div key={key}>
                        <label className={labelClass}>{label}</label>
                        <div className="flex items-center gap-1">
                          <input type="number" min="0" max="100" defaultValue={payments.cancellation_policy?.[key] ?? (key === '48h' ? 100 : key === '24h' ? 50 : 0)} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" id={`policy-${key}`} />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      const policy: Record<string, number> = {};
                      for (const k of ['48h', '24h', '0h']) {
                        const el = document.getElementById(`policy-${k}`) as HTMLInputElement | null;
                        if (el) policy[k] = parseInt(el.value, 10);
                      }
                      try {
                        await api.updatePaymentPolicy({ cancellation_policy: policy });
                        toast.success(t('settings.policySaved'));
                        setPayments((p) => p ? { ...p, cancellation_policy: policy } : null);
                      } catch (err) { if (err instanceof ApiError) toast.error(err.message); }
                    }}
                    className="mt-4 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    {t('settings.savePolicy')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── BILLING TAB ─────────────────────────────────────────── */}
      {activeTab === 'billing' && isOrgAdmin && (
        <div className="space-y-6 max-w-3xl">
          {billingLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {reconcileError && (
            <div className="flex items-start gap-3 p-4 rounded-xl border bg-red-50 border-red-200">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Billing reconcile failed — showing cached data</p>
                <p className="text-xs text-red-600 mt-1 font-mono break-all">{reconcileError}</p>
              </div>
            </div>
          )}

          {!billingLoading && billing && (
            <>
              {billing.status === 'trialing' && billing.trial_days_left !== null && (
                <div className={`flex items-start gap-3 p-4 rounded-xl border ${(billing.trial_days_left ?? 0) <= 3 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${(billing.trial_days_left ?? 0) <= 3 ? 'text-red-600' : 'text-amber-600'}`} />
                  <div>
                    <p className={`text-sm font-medium ${(billing.trial_days_left ?? 0) <= 3 ? 'text-red-800' : 'text-amber-800'}`}>
                      {billing.trial_days_left === 0 ? t('settings.trialEndsToday') : t('settings.trialEndsInDays', { days: billing.trial_days_left, plural: billing.trial_days_left !== 1 ? 's' : '' })}
                    </p>
                    <p className={`text-xs mt-0.5 ${(billing.trial_days_left ?? 0) <= 3 ? 'text-red-600' : 'text-amber-600'}`}>{t('settings.trialSubscribeHint')}</p>
                  </div>
                </div>
              )}

              {(billing.status === 'past_due' || billing.status === 'expired') && (
                <div className="flex items-start gap-3 p-4 rounded-xl border bg-red-50 border-red-200">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">{billing.status === 'expired' ? t('settings.subscriptionExpired') : t('settings.paymentFailed')}</p>
                    <p className="text-xs text-red-600 mt-0.5">{t('settings.updatePaymentMethod')}</p>
                  </div>
                </div>
              )}

              {billing.stripe_sync &&
                billing.stripe_sync.stripe_subscription_total > 0 &&
                billing.stripe_sync.platform_subscriptions_matched === 0 &&
                billing.stripe_sync.unmatched_price_ids.length > 0 && (
                <div className="flex items-start gap-3 p-4 rounded-xl border bg-amber-50 border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">{t('settings.stripePriceEnvMismatch')}</p>
                    <p className="text-xs text-amber-800 mt-1 whitespace-pre-wrap break-all">{t('settings.stripePriceEnvMismatchDetail', { ids: billing.stripe_sync.unmatched_price_ids.join(', ') })}</p>
                    {billing.stripe_sync.status_inferred_from_unmatched && (
                      <p className="text-xs text-amber-700 mt-1">{t('settings.statusInferredHint')}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{t('settings.currentPlan')}</p>
                    {billing.status === 'cancelled' && billing.tier !== 'free' ? (
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">{t('settings.noActiveSubscription')}</h2>
                        <p className="text-xs text-gray-500 mt-1 max-w-md">{t('settings.noActiveSubscriptionHint')}</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-gray-900">{TIER_LABELS[billing.tier] ?? billing.tier}</h2>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TIER_COLORS[billing.tier] ?? 'bg-gray-100 text-gray-700'}`}>{TIER_LABELS[billing.tier] ?? billing.tier}</span>
                      </div>
                    )}
                    <div className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border mt-2 ${STATUS_COLORS[billing.status] ?? 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                      {billing.status === 'active' && <CheckCircle className="w-3 h-3" />}
                      {billing.status === 'trialing' && <Zap className="w-3 h-3" />}
                      {['past_due', 'expired', 'cancelled'].includes(billing.status) && <AlertTriangle className="w-3 h-3" />}
                      <span className="capitalize">{billing.status.replace('_', ' ')}</span>
                    </div>
                  </div>
                  {billing.subscription_renews_at && billing.status === 'active' && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{t('settings.nextRenewal')}</p>
                      <p className="text-sm font-medium text-gray-700">{new Date(billing.subscription_renews_at).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                  )}
                </div>

                <div className="mt-5">
                  {billing.chat_limit > 0 ? (
                    <>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm text-gray-600 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" />{t('settings.aiChatsThisMonth')}</p>
                        <p className="text-sm font-medium text-gray-700">{billing.chats_used.toLocaleString()} / {billing.chat_limit.toLocaleString()}</p>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className={`h-2 rounded-full transition-all ${usagePct >= 90 ? 'bg-red-500' : usagePct >= 70 ? 'bg-amber-500' : 'bg-brand-500'}`} style={{ width: `${usagePct}%` }} />
                      </div>
                      {usagePct >= 90 && <p className="text-xs text-red-600 mt-1">{t('settings.approachingLimit')}</p>}
                    </>
                  ) : (
                    <p className="text-sm text-gray-600">{t('settings.aiChatNotOnFree')}</p>
                  )}
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  {billing.has_billing_account && (
                    <button onClick={handleManageBilling} disabled={actionLoading === 'portal'} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" />
                      {actionLoading === 'portal' ? t('settings.redirecting') : t('settings.manageBilling')}
                    </button>
                  )}
                </div>
              </div>

              {(billing.tier === 'free' || billing.status !== 'active' || billing.tier === 'starter' || billing.tier === 'growth') && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">{billing.tier === 'free' || billing.status === 'trialing' ? t('settings.choosePlan') : t('settings.upgradePlan')}</h3>
                  <div className="grid gap-4 sm:grid-cols-3">
                    {PLAN_OPTIONS.filter((p) => p.id !== billing.tier || billing.status !== 'active').map((plan) => {
                      const priceId = billing.price_ids[`${plan.id}_monthly`] ?? '';
                      const isCurrentActive = plan.id === billing.tier && billing.status === 'active';
                      return (
                        <div key={plan.id} className={`bg-white rounded-xl border p-5 shadow-sm flex flex-col ${plan.id === 'growth' ? 'border-brand-300 ring-1 ring-brand-300' : 'border-gray-200'}`}>
                          {plan.id === 'growth' && <span className="text-xs font-semibold bg-brand-600 text-white px-2 py-0.5 rounded-full self-start mb-2">{t('settings.popular')}</span>}
                          <h4 className="font-semibold text-gray-900">{plan.label}</h4>
                          <div className="mt-1 mb-3">
                            <span className="text-2xl font-bold text-gray-900">€{plan.price}</span>
                            <span className="text-xs text-gray-500">{t('settings.perMonth')}</span>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">{plan.description}</p>
                          <ul className="text-xs text-gray-600 space-y-1 mb-4 flex-1">
                            <li>✓ {plan.chats.toLocaleString()} {t('settings.aiChatsPerMonth')}</li>
                            <li>✓ {t('settings.upToMarinas', { count: plan.marinas, plural: plan.marinas > 1 ? 't' : '' })}</li>
                            <li>✓ {t('settings.bookingFormWidget')}</li>
                            <li>✓ {t('settings.emailNotifications')}</li>
                          </ul>
                          {isCurrentActive ? (
                            <span className="text-center text-xs text-green-700 font-medium py-2">{t('settings.currentPlanBadge')}</span>
                          ) : (
                            <button onClick={() => handleUpgrade(priceId)} disabled={!!actionLoading || !priceId} className="py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors">
                              {actionLoading === 'checkout' ? t('settings.redirecting') : t('settings.choosePlanButton', { plan: plan.label })}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mt-3">{t('settings.pricingExcludeTax')}</p>
                </div>
              )}

              {/* Stripe sync diagnostics (always visible for org_admins to debug) */}
              {billing.stripe_sync && (
                <details className="mt-4">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">Stripe sync diagnostics</summary>
                  <div className="mt-2 bg-gray-50 rounded-lg border border-gray-200 p-4 text-xs font-mono space-y-2 overflow-x-auto">
                    <p>Customer: <span className="text-gray-700">{billing.stripe_sync.customer_id}</span></p>
                    <p>Stripe subs total: <span className="text-gray-700">{billing.stripe_sync.stripe_subscription_total}</span> | Matched: <span className={billing.stripe_sync.platform_subscriptions_matched > 0 ? 'text-green-700' : 'text-red-600'}>{billing.stripe_sync.platform_subscriptions_matched}</span></p>
                    {billing.stripe_sync.status_inferred_from_unmatched && <p className="text-amber-600">Status inferred from unmatched Stripe subs (tier NOT updated)</p>}
                    {billing.stripe_sync.configured_price_ids && (
                      <div>
                        <p className="text-gray-500 mt-1">Configured STRIPE_PRICE_* env:</p>
                        {Object.entries(billing.stripe_sync.configured_price_ids).map(([k, v]) => (
                          <p key={k} className="pl-2">{k}: <span className={v ? 'text-gray-700' : 'text-red-400'}>{v || '(empty)'}</span></p>
                        ))}
                      </div>
                    )}
                    {billing.stripe_sync.subscriptions && billing.stripe_sync.subscriptions.length > 0 && (
                      <div>
                        <p className="text-gray-500 mt-1">Stripe subscriptions:</p>
                        {billing.stripe_sync.subscriptions.map((s) => (
                          <p key={s.sub_id} className="pl-2">
                            <span className={s.status === 'active' ? 'text-green-700' : s.status === 'canceled' ? 'text-gray-400' : 'text-amber-600'}>{s.status}</span>{' '}
                            {s.sub_id.substring(0, 20)}… price: <span className={s.matched ? 'text-green-700' : 'text-red-600'}>{s.price_id}</span>{' '}
                            {s.matched ? '✓' : '✗'}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'billing' && !isOrgAdmin && (
        <div className="text-center py-12 text-gray-500">
          <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{t('settings.billingManagedByOrg')}</p>
        </div>
      )}

      {/* ── Marina selection modal (downgrade) ────────────────────── */}
      {marinaSelection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{t('settings.selectMarinasTitle')}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('settings.selectMarinasDesc', { limit: marinaSelection.newLimit, total: marinaSelection.marinas.length })}
              </p>
            </div>
            <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
              {marinaSelection.marinas.map((m) => {
                const isSelected = selectedKeepIds.has(m.id);
                const atLimit = selectedKeepIds.size >= marinaSelection.newLimit && !isSelected;
                return (
                  <label
                    key={m.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-brand-300 bg-brand-50'
                        : atLimit
                          ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={atLimit}
                      onChange={() => {
                        setSelectedKeepIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(m.id)) next.delete(m.id);
                          else if (next.size < marinaSelection.newLimit) next.add(m.id);
                          return next;
                        });
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{m.name}</p>
                      <p className="text-xs text-gray-400 font-mono">{m.slug}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <button
                onClick={() => setMarinaSelection(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">
                  {selectedKeepIds.size}/{marinaSelection.newLimit}
                </span>
                <button
                  disabled={selectedKeepIds.size === 0 || !!actionLoading}
                  onClick={() => handleUpgrade(marinaSelection.priceId, [...selectedKeepIds])}
                  className="px-5 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading === 'checkout' ? t('settings.redirecting') : t('settings.confirmDowngrade')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
